import { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  NavLink,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  CalendarDays,
  Users,
  Settings as SettingsIcon,
  WifiOff,
  ArrowUpRight,
} from "lucide-react";
import { Provider, useStore } from "./store";
import { api } from "./api";
import { PwaProvider, usePwa } from "./pwa";
import { Schedule } from "./pages/Schedule";
import { Clients } from "./pages/Clients";
import { Settings } from "./pages/Settings";
import { ErrorText } from "./components/ui";
import "./styles.css";
function Brand() {
  return (
    <div className="brand">
      <img
        src="/brand/wordmark.png"
        width="1817"
        height="525"
        alt="Aethon Ink Tattoo Studio"
      />
    </div>
  );
}
function Connectivity() {
  const { connection, synced } = useStore();
  const label = {
    online: "Online",
    offline: "Offline",
    checking: "Checking connection",
    unavailable: "Server unavailable",
  }[connection];
  return (
    <div
      className={"connectivity " + connection}
      role="status"
      aria-live="polite"
      aria-label={label}
      title={
        synced
          ? "Last synchronized " + new Date(synced).toLocaleString()
          : "No synchronization yet"
      }
    >
      <span aria-hidden="true" />
      <small>{label}</small>
    </div>
  );
}
function Login() {
  const { refresh, error: connectionError } = useStore();
  const [username, setUsername] = useState(""),
    [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <main className="login">
      <div className="login-art">
        <Brand />
        <div>
          <p className="eyebrow">THE ART OF MAKING TIME</p>
          <h1>
            More space
            <br />
            for your
            <br />
            <em>next creation.</em>
          </h1>
          <span className="accent-rule" />
        </div>
        <p>A personal rhythm. A beautifully planned day.</p>
      </div>
      <section className="login-form">
        <Brand />
        <div>
          <p className="eyebrow">WELCOME BACK</p>
          <h2>Your day starts here.</h2>
          <p className="muted">Sign in to your personal appointment planner.</p>
        </div>
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              await api("/login", "POST", { username, password });
              await refresh();
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            Username
            <input
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <ErrorText text={error || connectionError} />
          <button className="primary wide" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
            <ArrowUpRight size={18} />
          </button>
        </form>
        <small className="muted">
          Aethon Ink · Your personal scheduling space
        </small>
      </section>
    </main>
  );
}
function App() {
  const { data, loading, offline, connection, synced } = useStore();
  const { update } = usePwa();
  if (loading)
    return (
      <div className="loading">
        <Brand />
        <p>Preparing your day…</p>
      </div>
    );
  if (!data) return <Login />;
  return (
    <div className="app">
      <aside className="sidebar">
        <Brand />
        <div className="sidebar-caption">YOUR PERSONAL SPACE</div>
        <nav aria-label="Main navigation">
          {[
            { to: "/schedule", label: "Schedule", Icon: CalendarDays },
            { to: "/clients", label: "Clients", Icon: Users },
            { to: "/settings", label: "Settings", Icon: SettingsIcon },
          ].map(({ to, label, Icon }) => (
            <NavLink key={to} to={to}>
              <Icon size={21} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <Connectivity />
        </div>
      </aside>
      <div className="main-wrap">
        <header className="mobile-header">
          <Brand />
          <Connectivity />
        </header>
        {offline && (
          <div className="offline" role="status">
            <WifiOff size={17} />
            <span>
              {connection === "offline"
                ? "Offline · Changes are disabled."
                : connection === "checking"
                  ? "Checking connection · Changes are disabled."
                  : "Server unavailable · Changes are disabled."}
              <small>
                {synced
                  ? "Last synced " + new Date(synced).toLocaleString()
                  : ""}
              </small>
            </span>
          </div>
        )}
        {update && (
          <div className="update-banner">
            A fresh version is ready.
            <button onClick={() => update.postMessage("SKIP_WAITING")}>
              Update now
            </button>
          </div>
        )}
        <main className="content">
          <Routes>
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/schedule" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
createRoot(document.getElementById("root")!).render(
  <BrowserRouter>
    <Provider>
      <PwaProvider>
        <App />
      </PwaProvider>
    </Provider>
  </BrowserRouter>,
);
